import { useState } from "react";
import { Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface EmailDisclaimerDialogProps {
  email: string;
  attorneyName: string;
  variant?: "default" | "compact" | "inline";
}

const EmailDisclaimerDialog = ({ email, attorneyName, variant = "default" }: EmailDisclaimerDialogProps) => {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleAcceptAndEmail = () => {
    window.location.href = `mailto:${email}`;
    setOpen(false);
    setAccepted(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setAccepted(false);
  };

  const isCompact = variant === "compact";
  const isInline = variant === "inline";

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={
          isInline
            ? "text-primary hover:underline transition-colors inline"
            : isCompact
              ? "flex items-center justify-center gap-1.5 text-xs font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-3 py-2 transition-colors tracking-[0.1em] uppercase flex-1"
              : "flex items-center justify-center gap-2 text-sm font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-4 py-3 transition-colors tracking-[0.1em] uppercase mb-3 w-64"
        }
      >
        {isInline ? email : (
          <>
            <Mail className={isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} />
            Email
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Email Disclaimer</DialogTitle>
            <DialogDescription className="font-body text-sm leading-relaxed pt-2">
              Please do not send an email or any communications containing confidential or
              sensitive information if you have not already retained our firm to represent you.
              Sending an email does not create an attorney-client relationship. Please accept
              these terms to continue sending an email.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-3 py-2">
            <Checkbox
              id="email-terms"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
            />
            <label
              htmlFor="email-terms"
              className="text-sm font-body text-foreground cursor-pointer leading-snug"
            >
              I accept these terms
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="font-body text-sm"
            >
              Cancel
            </Button>
            <Button
              disabled={!accepted}
              onClick={handleAcceptAndEmail}
              className="font-body text-sm"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailDisclaimerDialog;
